import React, {MouseEvent, DragEvent, ReactElement} from 'react'
import cx from 'classnames'
import styles from './Card.module.scss'
import Image from '../Image'
import Button from '../Button'

type CardProps = {
    img?: {
        src: string
        alt: string
    }
    title?: string
    children: React.ReactNode | React.ReactNode[]
    classes?: string
    innerClasses?: string
    onClick?: (e: MouseEvent<HTMLDivElement>) => void
    draggable?: boolean
    onDrag?: (e: DragEvent<HTMLDivElement>) => void
    actions?: {
        title: string
        onAction: () => void
        type?: 'primary' | 'secondary' | 'tertiary'
        classes?: string
    }[]
    hoverEffect?: boolean
    style?: React.CSSProperties,
    boxShadow?: boolean
}

type SlotProps = {
    children: React.ReactNode | React.ReactNode[]
    style?: React.CSSProperties
}

type CardComponent = React.FC<CardProps> & {
    Header: React.FC<SlotProps>
}

const Card: CardComponent = ({
    classes,
    innerClasses,
    title,
    img,
    children,
    onClick,
    draggable = false,
    onDrag,
    actions,
    hoverEffect = false,
    style = undefined,
}) => {
    const childArray = React.Children.toArray(children) as ReactElement[];

    const header = childArray.find(el => el.type === Card.Header)
    const cardContent = childArray.filter(
        el => el.type !== Card.Header
    );

    const onClickEventHandler = (e: MouseEvent<HTMLDivElement>) => {
        if (onClick !== undefined) onClick(e)
    }

    const onDragEventHandler = (e: DragEvent<HTMLDivElement>) => {
        if (onDrag !== undefined) onDrag(e)
    }

    return (
        <div
            className={cx(
                hoverEffect ? styles['zr-card-hover'] : styles['zr-card'],
                classes
            )}
            draggable={draggable}
            style={style}
            onDrag={(event) => onDragEventHandler(event)}
        >
            {header}
            <div onClick={(event) => onClickEventHandler(event)}>
                {img && <Image src={img.src} alt={img.alt} width={'100%'} />}
                <div className={cx(styles['zr-card-inner'], innerClasses)}>
                    {title && (
                        <p className={cx(styles['zr-card-title'])}>{title}</p>
                    )}
                    {cardContent}
                </div>
            </div>
            {actions && actions.length > 0 && (
                <div className={cx(styles['zr-card-actions'])}>
                    {actions.map((action, index) => {
                        return (
                            <Button
                                key={index}
                                hierarchy={action.type ?? 'primary'}
                                text={action.title}
                                type={'button'}
                                classes={action.classes}
                                fn={action.onAction}
                            />
                        )
                    })}
                </div>
            )}
        </div>
    )
}

Card.Header = function Header({ children, style }: SlotProps ) {
    return <div className={cx(styles['zr-card-header'])} style={style}>{children}</div>;
};

export default Card